import { Heading, ProgressCircle, Text } from '@react-spectrum/s2';
import { style as spectrumStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon.tsx';
import { CloseIcon } from '@/components/icons/CloseIcon.tsx';
import {
	getChatPanelLabel,
	shouldShowBackChevron,
} from '@/spaces/chat/panels/panelLabel';
import { cn } from '@/utils/cn.ts';

interface ChatPanelLayoutProps {
	title: string;
	description?: string;
	headerTagRender?: () => React.ReactNode;
	actionRenderer?: () => React.ReactNode;
	isLoading?: boolean;
	onBack?: () => void;
	backLabel?: string;
	onClose: () => void;
	className?: string;
	style?: React.CSSProperties;
	children: React.ReactNode;
}

export const ChatPanelLayout = ({
	title,
	description,
	headerTagRender,
	actionRenderer,
	isLoading,
	onBack,
	backLabel,
	onClose,
	className,
	style: containerStyle,
	children,
}: ChatPanelLayoutProps) => {
	const bridgeApi = useBridgeApi();
	const history = bridgeApi.user_state?.chat_panel_history ?? [];
	const defaultBack = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
	}, [bridgeApi]);

	const showChevron = shouldShowBackChevron(history);
	const resolvedBack = onBack ?? (showChevron ? defaultBack : undefined);
	const resolvedBackLabel =
		backLabel ??
		(showChevron ? getChatPanelLabel(history[history.length - 1]) : undefined);

	const headerTag = headerTagRender?.();
	const actions = isLoading ? null : actionRenderer?.();

	const openPanel = bridgeApi.user_state?.chat_panel;

	return (
		<div
			data-testid="chat-panel"
			data-panel-item-id={openPanel?.item_id}
			data-panel-chat-id={openPanel?.chat_id}
			className={cn(
				'glass-panel flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
				className,
			)}
			style={containerStyle}
		>
			<div
				className="flex shrink-0 items-center justify-between gap-3 px-6 pt-3
					pb-2"
			>
				<div className="flex min-w-0 flex-1 items-center gap-2">
					{resolvedBack && (
						<button
							type="button"
							onClick={resolvedBack}
							aria-label={
								resolvedBackLabel ? `Back to ${resolvedBackLabel}` : 'Back'
							}
							title={
								resolvedBackLabel ? `Back to ${resolvedBackLabel}` : 'Back'
							}
							className="-ml-1 flex shrink-0 items-center rounded-md p-1
								text-gray-500 transition-colors hover:bg-gray-100
								hover:text-gray-700"
						>
							<ChevronLeftIcon className="size-5 shrink-0" />
						</button>
					)}
					<Heading
						level={2}
						styles={spectrumStyle({
							font: 'heading',
							fontSize: 'heading-sm',
							fontWeight: 'bold',
						})}
						UNSAFE_className="min-w-0 flex-1 truncate"
					>
						{title}
					</Heading>
				</div>
				<div className="flex shrink-0 items-center gap-2">
					{headerTag}
					<button
						type="button"
						onClick={onClose}
						aria-label="Close panel"
						className="rounded-full p-1 text-gray-500 transition-colors
							hover:bg-gray-100 hover:text-gray-700"
					>
						<CloseIcon className="size-5" />
					</button>
				</div>
			</div>

			<div
				className={cn(
					'flex min-h-0 min-w-0 flex-1 flex-col px-6',
					actions != null ? 'pb-1' : 'pb-6',
				)}
			>
				{description && (
					<Text
						styles={spectrumStyle({
							font: 'body',
							fontSize: 'body-sm',
							color: 'body',
						})}
						UNSAFE_className="mb-4 line-clamp-4"
					>
						{description}
					</Text>
				)}
				<div className="flex min-h-0 min-w-0 flex-1 flex-col">
					{isLoading ? (
						<div className="flex min-h-0 flex-1 items-center justify-center">
							<div className="flex items-center gap-2">
								<ProgressCircle
									isIndeterminate
									size="S"
									staticColor="auto"
									aria-label="Loading artifact"
								/>
								<Text
									styles={spectrumStyle({
										font: 'body',
										fontSize: 'body-sm',
										color: 'detail',
									})}
								>
									Loading artifact...
								</Text>
							</div>
						</div>
					) : (
						<div
							className="scrollbar-hidden flex min-h-0 min-w-0 flex-1 flex-col
								overflow-auto"
						>
							{children}
						</div>
					)}
				</div>
			</div>

			{actions != null && (
				<div className="flex items-center justify-end gap-2 px-6 pt-1 pb-3">
					{actions}
				</div>
			)}
		</div>
	);
};
